// src/components/Inbox.tsx
import React, { useMemo } from "react";
import { Mounter } from "./Mounter.js";
import { useNovuUI } from "../context/NovuUIContext.js";
import { useRenderer } from "../context/RendererContext.js";
import { InternalNovuProvider, useNovu, useUnsafeNovu } from "../hooks/NovuProvider.js";
import { NovuUI } from "./NovuUI.js";
import { withRenderer } from "./Renderer.js";
import { jsx } from "react/jsx-runtime";
var DefaultInbox = (props) => {
  const {
    open,
    renderNotification,
    renderSubject,
    renderBody,
    renderBell,
    onNotificationClick,
    onPrimaryActionClick,
    onSecondaryActionClick,
    placement,
    placementOffset
  } = props;
  const { novuUI } = useNovuUI();
  const { mountElement } = useRenderer();
  const mount = React.useCallback(
    (element) => {
      if (renderNotification) {
        return novuUI.mountComponent({
          name: "Inbox",
          props: {
            open,
            renderNotification: renderNotification ? (el, notification) => mountElement(el, renderNotification(notification)) : void 0,
            renderBell: renderBell ? (el, unreadCount) => mountElement(el, renderBell(unreadCount)) : void 0,
            onNotificationClick,
            onPrimaryActionClick,
            onSecondaryActionClick,
            placementOffset,
            placement
          },
          element
        });
      }
      return novuUI.mountComponent({
        name: "Inbox",
        props: {
          open,
          renderSubject: renderSubject ? (el, notification) => mountElement(el, renderSubject(notification)) : void 0,
          renderBody: renderBody ? (el, notification) => mountElement(el, renderBody(notification)) : void 0,
          renderBell: renderBell ? (el, unreadCount) => mountElement(el, renderBell(unreadCount)) : void 0,
          onNotificationClick,
          onPrimaryActionClick,
          onSecondaryActionClick,
          placementOffset,
          placement
        },
        element
      });
    },
    [
      open,
      renderNotification,
      renderSubject,
      renderBody,
      renderBell,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick
    ]
  );
  return /* @__PURE__ */ jsx(Mounter, { mount });
};
var Inbox = React.memo((props) => {
  const { applicationIdentifier, subscriberHash, backendUrl, socketUrl } = props;
  const novu = useUnsafeNovu();
  if (novu) {
    return /* @__PURE__ */ jsx(InboxChild, { ...props });
  }
  return /* @__PURE__ */ jsx(
    InternalNovuProvider,
    {
      applicationIdentifier,
      subscriberHash,
      backendUrl,
      socketUrl,
      subscriber: buildSubscriber(props),
      userAgentType: "components",
      children: /* @__PURE__ */ jsx(InboxChild, { ...props })
    }
  );
});
var InboxChild = withRenderer(
  React.memo((props) => {
    const {
      localization,
      appearance,
      tabs,
      preferencesFilter,
      preferenceGroups,
      routerPush,
      applicationIdentifier,
      subscriberId,
      subscriberHash,
      backendUrl,
      socketUrl
    } = props;
    const novu = useNovu();
    const options = useMemo(() => {
      return {
        localization,
        appearance,
        tabs,
        preferencesFilter,
        preferenceGroups,
        routerPush,
        options: {
          applicationIdentifier,
          subscriberHash,
          backendUrl,
          socketUrl,
          subscriber: buildSubscriber(props)
        }
      };
    }, [
      localization,
      appearance,
      tabs,
      preferencesFilter,
      preferenceGroups,
      applicationIdentifier,
      subscriberId,
      subscriberHash,
      backendUrl,
      socketUrl,
      props.subscriber
    ]);
    if (isWithChildrenProps(props)) {
      return /* @__PURE__ */ jsx(NovuUI, { options, novu, children: props.children });
    }
    const {
      open,
      renderNotification,
      renderSubject,
      renderBody,
      renderBell,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick,
      placementOffset,
      placement
    } = props;
    return /* @__PURE__ */ jsx(NovuUI, { options, novu, children: /* @__PURE__ */ jsx(
      DefaultInbox,
      {
        open,
        renderNotification,
        renderSubject,
        renderBody,
        renderBell,
        onNotificationClick,
        onPrimaryActionClick,
        onSecondaryActionClick,
        placement,
        placementOffset
      }
    ) });
  })
);
function isWithChildrenProps(props) {
  return "children" in props;
}
function buildSubscriber(options) {
  let subscriberObj;
  if (options.subscriber) {
    subscriberObj = typeof options.subscriber === "string" ? { subscriberId: options.subscriber } : options.subscriber;
  } else {
    subscriberObj = { subscriberId: options.subscriberId };
  }
  return subscriberObj;
}
export {
  Inbox
};
//# sourceMappingURL=Inbox.js.map